/*
 * iiimagine-frontend - Jobs.tsx
 * Job data and type definitions
 *
 * Updated for nested whatYouWillBeDoing structure (intro, tasks, outro)
 */

import React from "react";

// Define flexible shape for whatYouWillBeDoing
export type WhatDoing =
  | string[]
  | {
      intro: string;
      tasks: string[];
      outro?: string;
    };

// Define the Job interface
export interface Job {
  title: string;
  description: React.ReactNode;
  period?: string;
  whatYouWillBeDoing: WhatDoing;
  requirements: string[];
  video?: {
    url: string;
    thumbnail?: string;
  };
  interested: React.ReactNode;
}

// Define the list of jobs
export const jobs: Job[] = [


  {
    title: "Carpenter / Schrijnwerker",
    description: (
      <>
        <p>
          Behind the scenes, we are working on our most ambitious project yet:{" "}
          <a
            href="https://prodigy12.com/"
            target="_blank"
            className="text-iiimagine-red"
            rel="noreferrer"
          >
            Prodigy12
          </a>{" "}
          will be a 360° live spectacle in a futuristic, traveling pavilion that
          we are building.
        </p>

        <p>
          In the coming weeks, our production team will be active in Ghent and Aalter, preparing materials and building elements. We are looking for a super motivated, fast-paced carpenter to join us on a flexible basis.
        </p>

        <p>
          Directed by the Roelandt Brothers, we are builders as much as
          creators. Beyond creating the screenplay, visuals, and music, we
          physically construct the production ourselves — from the stage
          structure and rigging to light, sound, and LED. We also oversee
          ticketing and operations, bringing the full experience directly to the
          audience, without intermediaries.
        </p>

        <p>
          We are looking for someone who is not only a good person but also
          strives for excellence in what they do. Together, we believe that a
          strong team can accomplish a great deal. People who are motivated,
          driven to build, curious to learn, and eager to join the journey. We
          have a clear vision for Prodigy12 and will do our best to make you
          fly.
        </p>
      </>
    ),

    period: "Freelance and/or Student",

    whatYouWillBeDoing: {
      intro: "You will be involved in all aspects of live production, including:",
      tasks: [
        "Handling large-scale panels (3m x 1m) on assembly tables.",
        "Working with a plunge saw and milling tools for precise grooves.",
        "Executing technical finishing tasks including corner cutting and edge folding.",
        "Assembling and securing aluminium profiles using clamps and drills.",
        "Finalizing components through riveting and bolting.",
        "Organizing and palletizing finished elements for logistics.",
      ],
      outro: "while being guided by our Head of Production.",
    },

    requirements: [
      "Super motivated and hands-on mentality.",
      "Physically fit to handle 30kg panels.",
      "Proven experience working with power tools (plunge saw and drill).",
      "Flexible availability during the coming days/weeks.",
      "Based in or near Ghent.",
      "Driver license is a big advantage.",
    ],

    interested: (
      <>
        <p>
          Phase 1: Please email your CV and cover letter to{" "}
          <a
            href="mailto:production@prodigy12.com"
            className="text-iiimagine-red"
          >
            production@prodigy12.com
          </a>
          <br />
          Phase 2: 30-minute telephone interview         
        </p>
        <br />
        <p>Looking forward to reviewing your application!</p>
      </>
    ),
  },


  {
    title: "Production Crew",
    description: (
      <>
        <p>
          Behind the scenes, we are working on our most ambitious project yet:{" "}
          <a
            href="https://prodigy12.com/"
            target="_blank"
            className="text-iiimagine-red"
            rel="noreferrer"
          >
            Prodigy12
          </a>{" "}
          will be a 360° live spectacle in a futuristic, traveling pavilion that
          we are building.
        </p>

        <p>
          This is your opportunity to join a fast-growing creative startup that
          aims to redefine how people experience live shows.
        </p>

        <p>
          Directed by the Roelandt Brothers, we are builders as much as
          creators. Beyond creating the screenplay, visuals, and music, we
          physically construct the production ourselves — from the stage
          structure and rigging to light, sound, and LED. We also oversee
          ticketing and operations, bringing the full experience directly to the
          audience, without intermediaries.
        </p>

        <p>
          We are looking for someone who is not only a good person but also
          strives for excellence in what they do. Together, we believe that a
          strong team can accomplish a great deal. People who are motivated,
          driven to build, curious to learn, and eager to join the journey. We
          have a clear vision for Prodigy12 and will do our best to make you
          fly.
        </p>
      </>
    ),

    period: "Freelance",

    whatYouWillBeDoing: {
      intro: "You will be involved in all aspects of live production, including:",
      tasks: [
        "Scaffolding",
        "Rigging",
        "LED and video setup",
        "Truss and stage construction",
        "Lighting",
        "Sound",
        "Electricity",
      ],
      outro: "while being guided by our Head of Production.",
    },

    requirements: [
      "Strong curiosity and a desire to create something exceptional",
      "A strong work ethic, we expect the same commitment from you as from the team",
      "High attention to detail",
      "Bonus: Experience working with wood or metal",
    ],

    interested: (
      <>
        <p>
          Phase 1: Please email your CV, cover letter, and (if available)
          portfolio to{" "}
          <a
            href="mailto:production@prodigy12.com"
            className="text-iiimagine-red"
          >
            production@prodigy12.com
          </a>
          <br />
          Phase 2: 30-minute telephone interview
          <br />
          Phase 3: Test
        </p>
        <br />
        <p>Looking forward to reviewing your application!</p>
      </>
    ),
  },
  
  
    {
    title: "Production Assistants Ghent (Students)",
    description: (
      <>

        <p>
          Behind the scenes, we are working on our most ambitious project yet:{" "}
          <a
            href="https://prodigy12.com/"
            target="_blank"
            className="text-iiimagine-red"
            rel="noreferrer"
          >
            Prodigy12
          </a>{" "}
          will be a 360° live spectacle in a futuristic, traveling pavilion that
          we are building.
        </p>


        <p>
          Prodigy12 is a large-scale immersive live show premiering in Paris in
          early 2026.
        </p>

        <p>
          In the coming weeks, our production team will be active in Ghent,
          preparing materials, building elements, organising logistics and
          supporting our artistic &amp; technical departments.
        </p>

        <p>
          We are looking for motivated students who want to join us on a
          flexible basis and support a variety of hands-on tasks.
        </p>

        <p>
          <strong>Practical info:</strong>
        </p>
        <ul className="list-disc ml-6">
          <li>Location: Ghent – various workspaces/locations</li>
          <li>Timing: the coming weeks and months</li>
          <li>Paid student job</li>
          <li>Hours depend on production needs</li>
        </ul>
      </>
    ),

    period: "Student job – Ghent",

    whatYouWillBeDoing: {
      intro: "What you will be doing:",
      tasks: [
        "Helping prepare and organise materials for the show",
        "Build-up tasks",
        "Packing, moving or arranging equipment",
        "Helping with production logistics",
        "Being part of a dynamic, hands-on environment",
      ],
    },

    requirements: [
      "Responsible, punctual and proactive",
      "Hands-on mentality and not afraid to help wherever needed",
      "Flexible availability",
      "Based in or near Ghent",
      "Driver’s license is a big advantage",
      "Interest in production, live events, theatre or design is a plus",
    ],

    interested: (
      <>
        <p>
          Send a short message with your motivation, availability, and CV to{" "}
          <a
            href="mailto:jobs@prodigy12.com"
            className="text-iiimagine-red"
          >
            jobs@prodigy12.com
          </a>
          .
        </p>
        <p>We review applications continuously.</p>
      </>
    ),
  },
];
